import { useEffect, useRef, useState } from "react";
import { GraduationCap } from "lucide-react";
import { theme, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { SheetCard, DimensionHeading } from "../../components/Drawing";
import { SeverityPill, secondaryBtn, textarea, fmtDay } from "./shared";
import type { ProposalAudience } from "./PhishingTrainingProposals";

// Training proposed from competence gaps (backend lib/competenceTrainingProposals.ts). A member holding a
// role in the competence register (ISO 7.2) with a gap, still developing, or with lapsed evidence is
// offered a course. "Review and assign" opens the ordinary assignment form pre-filled with the
// requirement it answers; nothing is assigned until it is submitted. A dismissal needs a reason.

export interface CompetenceProposal {
  id: string;
  requirementId: string;
  ref: string;
  role: string;
  competence: string;
  people: Array<{ userId: string; name: string; standing: "gap" | "developing" | "expired"; actionDueAt: string | null }>;
  severity: "high" | "medium";
  reasons: string[];
  suggested: { title: string; kind: string; description: string; dueInDays: number; audience: ProposalAudience; competenceRequirementId: string };
}

export interface CompetenceProposalView {
  proposals: CompetenceProposal[];
  dismissed: Array<{ id: string; requirementId: string; ref: string; role: string; reason: string; dismissedAt: string; dismissedByName: string | null }>;
}

const STANDING: Record<string, string> = { gap: "gap", developing: "developing", expired: "evidence expired" };

export function CompetenceTrainingProposals({ view, canEdit, focusRequirementId, onAssign, onChanged }: {
  view: CompetenceProposalView;
  canEdit: boolean;
  /** ?competence=<requirementId>, from the My Actions item. */
  focusRequirementId: string | null;
  onAssign: (p: CompetenceProposal) => void;
  onChanged: (notice: string) => Promise<void>;
}) {
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { focusRef.current?.scrollIntoView({ block: "center" }); }, [focusRequirementId]);

  if (view.proposals.length === 0 && view.dismissed.length === 0) return null;

  async function dismiss(p: CompetenceProposal) {
    setBusy(true); setError(null);
    try {
      await api.post(`/training-assignments/competence-proposals/${encodeURIComponent(p.requirementId)}/dismiss`, { reason: reason.trim() });
      setDismissing(null); setReason("");
      await onChanged(`Proposal for ${p.role} dismissed. It comes back if anyone's competence record changes.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not dismiss the proposal")); }
    finally { setBusy(false); }
  }

  async function restore(id: string, role: string) {
    setBusy(true); setError(null);
    try {
      await api.post(`/training-assignments/competence-proposals/dismissals/${encodeURIComponent(id)}/withdraw`, {});
      await onChanged(`Dismissal withdrawn for ${role}.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not withdraw the dismissal")); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <DimensionHeading as="h3" count={view.proposals.length}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><GraduationCap size={15} /> Proposed from competence gaps</span>
      </DimensionHeading>
      <div style={{ ...text.hint, marginTop: -4, marginBottom: 10 }}>
        People recorded in the <PortalLink to="/management-system/competence" style={{ color: theme.color.ref }}>competence register</PortalLink> with a gap, still developing, or whose evidence has expired, for a competence gained by training or a qualification. Nothing is assigned until you confirm it.
      </div>
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "grid", gap: 10 }}>
        {view.proposals.map((p) => {
          const focused = p.requirementId === focusRequirementId;
          return (
            <div key={p.id} ref={focused ? focusRef : undefined}>
              <SheetCard featured={focused} label={`Training for ${p.role}`} reference={p.ref} actions={<SeverityPill value={p.severity} />}>
                <div style={{ ...text.body, marginBottom: 6 }}>{p.competence}</div>
                <ul style={{ ...text.body, margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
                  {p.people.map((x) => (
                    <li key={x.userId}>{x.name}: {STANDING[x.standing] ?? x.standing}{x.actionDueAt ? `, action due ${fmtDay(x.actionDueAt)}` : ""}</li>
                  ))}
                </ul>
                <div style={{ ...text.meta, marginTop: 6 }}>
                  Proposed course: {p.suggested.title}, due in {p.suggested.dueInDays} days, assigned to {p.people.length === 1 ? p.people[0].name : `these ${p.people.length} people`}. You can change any of it before assigning.
                </div>
                {canEdit && dismissing !== p.id && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button type="button" style={btnPrimary} disabled={busy} onClick={() => onAssign(p)}>Review and assign</button>
                    <button type="button" style={secondaryBtn} disabled={busy} onClick={() => { setDismissing(p.id); setReason(""); setError(null); }}>Dismiss</button>
                  </div>
                )}
                {canEdit && dismissing === p.id && (
                  <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                    <label style={text.label} htmlFor={`dismiss-${p.id}`}>Why is no training needed?</label>
                    <textarea id={`dismiss-${p.id}`} rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} style={textarea}
                      placeholder="For example, they are booked on an external course next month" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" style={btnPrimary} disabled={busy || reason.trim().length < 3} onClick={() => void dismiss(p)}>{busy ? "Dismissing…" : "Dismiss proposal"}</button>
                      <button type="button" style={secondaryBtn} disabled={busy} onClick={() => setDismissing(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </SheetCard>
            </div>
          );
        })}
      </div>
      {view.dismissed.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ ...text.meta, cursor: "pointer" }}>Dismissed competence proposals ({view.dismissed.length})</summary>
          <ul style={{ ...text.body, margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 6 }}>
            {view.dismissed.map((d) => (
              <li key={d.id}>
                <strong>{d.role}</strong> ({d.ref}), {fmtDay(d.dismissedAt)}{d.dismissedByName ? ` by ${d.dismissedByName}` : ""}: {d.reason}
                {canEdit && <button type="button" style={{ ...secondaryBtn, marginLeft: 8, padding: "2px 8px" }} disabled={busy} onClick={() => void restore(d.id, d.role)}>Withdraw dismissal</button>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
